import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, BarChart3, Bookmark, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Distinct colours per project, cycled. Picked from the existing app palette.
const COLOURS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#ea580c', '#65a30d'];
const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtH = (n) => `${(n || 0).toFixed(1)}h`;

export const ProjectComparisonDialog = ({ open, onOpenChange, projects = [] }) => {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Saved comparisons (named project cohorts the user re-uses).
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadSaved = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/reports/saved-comparisons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSaved(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    if (open) loadSaved();
  }, [open, loadSaved]);

  const applySaved = (entry) => {
    // Only include projects that still exist (filter out deleted ones).
    const validIds = new Set(projects.map(p => p.id));
    const next = new Set(entry.project_ids.filter(id => validIds.has(id)));
    if (next.size < 2) {
      toast.error('This saved view has fewer than 2 valid projects', {
        description: 'Some projects may have been deleted. Pick again.',
      });
      return;
    }
    setSelected(next);
    setData(null);
    toast.success(`Loaded "${entry.name}" — ${next.size} projects`);
  };

  const saveCurrent = async () => {
    if (selected.size < 2) {
      toast.error('Pick at least 2 projects to save');
      return;
    }
    const name = prompt('Name this comparison (e.g., "Q1 2026 Insulation Jobs")');
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/reports/saved-comparisons`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), project_ids: [...selected] })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Saved');
      await loadSaved();
    } catch (e) {
      toast.error('Could not save', { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const removeSaved = async (id) => {
    if (!window.confirm('Delete this saved comparison?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/reports/saved-comparisons/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSaved((prev) => prev.filter(s => s.id !== id));
    } catch (e) {
      toast.error('Could not delete', { description: e.message });
    }
  };

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...projects]
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [projects, search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const generate = async () => {
    if (selected.size < 2) {
      toast.error('Pick at least 2 projects to compare');
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      [...selected].forEach((id) => params.append('project_ids', id));
      const res = await fetch(`${API_URL}/api/reports/project-comparison?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      toast.error('Could not load comparison', { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  // Build chart-friendly slices.
  const barData = useMemo(() => (data?.projects || []).map((p) => ({
    name: p.project_name.length > 22 ? p.project_name.slice(0, 22) + '…' : p.project_name,
    'Direct labour $': p.labour_direct_pay,
    'Subcontractor $': p.labour_subcontractor_pay,
    'Material $': p.material_cost,
  })), [data]);

  const hoursData = useMemo(() => (data?.projects || []).map((p) => ({
    name: p.project_name.length > 22 ? p.project_name.slice(0, 22) + '…' : p.project_name,
    Hours: p.hours,
  })), [data]);

  const pieData = useMemo(() => (data?.projects || []).map((p) => ({
    name: p.project_name,
    value: p.total_cost,
  })), [data]);

  const reset = () => { setData(null); setSelected(new Set()); setSearch(''); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        data-testid="project-comparison-dialog"
        className={data ? 'sm:max-w-[95vw] max-h-[92vh] overflow-y-auto' : 'sm:max-w-2xl'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Compare Projects
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start</Label>
              <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setData(null); }} data-testid="compare-start-date" />
            </div>
            <div>
              <Label>End</Label>
              <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setData(null); }} data-testid="compare-end-date" />
            </div>
          </div>

          {/* Project selector */}
          {!data && (
            <>
              {/* Saved comparisons strip — shown above the picker for fast re-use */}
              {saved.length > 0 && (
                <div className="border rounded-lg bg-amber-50/40 px-3 py-2">
                  <div className="text-[11px] font-semibold text-amber-900 mb-2 flex items-center gap-1">
                    <Bookmark className="w-3 h-3" /> Saved comparisons
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {saved.map((s) => (
                      <div key={s.id} className="inline-flex items-center gap-1 bg-white border border-amber-200 rounded-full text-xs">
                        <button
                          type="button"
                          onClick={() => applySaved(s)}
                          className="pl-3 pr-1 py-1 hover:text-purple-700"
                          data-testid={`saved-comparison-${s.id}`}
                          title={`${s.project_ids.length} projects · saved by ${s.created_by_name || 'unknown'}`}
                        >
                          {s.name}
                          <span className="ml-1 text-slate-400">({s.project_ids.length})</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSaved(s.id)}
                          className="px-1.5 py-1 text-slate-400 hover:text-red-600"
                          aria-label={`Delete ${s.name}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border rounded-lg">
              <div className="px-3 py-2 bg-slate-50 border-b flex items-center gap-2">
                <Input
                  placeholder="Search projects…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="compare-project-search"
                />
                <span className="text-xs text-slate-600 whitespace-nowrap">
                  {selected.size} selected
                </span>
                {selected.size > 0 && (
                  <Button type="button" size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-7 text-xs">
                    Clear
                  </Button>
                )}
              </div>
              <div className="max-h-[40vh] overflow-y-auto">
                {filteredProjects.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center">No projects match</div>
                ) : filteredProjects.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={selected.has(p.id)}
                      onCheckedChange={() => toggle(p.id)}
                      data-testid={`compare-project-${p.id}`}
                    />
                    <span className="flex-1 truncate">{p.name}</span>
                    {p.status && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                        {p.status}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
            </>
          )}

          {/* Charts + table */}
          {data && data.projects && data.projects.length > 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Stacked-bar: labour split + material */}
                <div className="border rounded-lg p-3 lg:col-span-2">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Cost breakdown</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={barData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Direct labour $" stackId="cost" fill="#2563eb" />
                      <Bar dataKey="Subcontractor $" stackId="cost" fill="#7c3aed" />
                      <Bar dataKey="Material $" stackId="cost" fill="#d97706" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Pie: total cost share */}
                <div className="border rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Total cost share</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label={(e) => fmt(e.value)}>
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={COLOURS[i % COLOURS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Hours-only chart */}
              <div className="border rounded-lg p-3">
                <div className="text-xs font-semibold text-slate-700 mb-2">Man hours</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={hoursData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}h`} />
                    <Tooltip formatter={(v) => fmtH(v)} />
                    <Bar dataKey="Hours" fill="#059669" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Raw numbers table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b text-xs font-semibold text-slate-700">
                  Numbers · {data.currency} · {startDate} → {endDate}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="compare-numbers-table">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-2 py-2 text-left">Project</th>
                        <th className="px-2 py-2 text-right">Hours</th>
                        <th className="px-2 py-2 text-right">Direct $</th>
                        <th className="px-2 py-2 text-right">Sub $</th>
                        <th className="px-2 py-2 text-right">Labour $</th>
                        <th className="px-2 py-2 text-right">Material $</th>
                        <th className="px-2 py-2 text-right">Total $</th>
                        <th className="px-2 py-2 text-right">$ / hr</th>
                        <th className="px-2 py-2 text-right">Workers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projects.map((p, i) => (
                        <tr key={p.project_id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-2 py-1.5 font-medium">
                            <span className="inline-block w-2.5 h-2.5 rounded-sm mr-2 align-middle" style={{ backgroundColor: COLOURS[i % COLOURS.length] }} />
                            {p.project_name}
                          </td>
                          <td className="px-2 py-1.5 text-right">{p.hours.toFixed(1)}</td>
                          <td className="px-2 py-1.5 text-right text-blue-700">${p.labour_direct_pay.toFixed(0)}</td>
                          <td className="px-2 py-1.5 text-right text-purple-700">${p.labour_subcontractor_pay.toFixed(0)}</td>
                          <td className="px-2 py-1.5 text-right">${p.labour_total_pay.toFixed(0)}</td>
                          <td className="px-2 py-1.5 text-right text-amber-700">${p.material_cost.toFixed(0)}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">${p.total_cost.toFixed(0)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-600">${p.cost_per_hour.toFixed(0)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-600">{p.employee_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 italic">
                Material cost is from material orders placed in this window. Once invoice OCR is enabled, this auto-switches to invoiced amounts.
              </div>
            </div>
          )}

          {data && data.projects && data.projects.length === 0 && (
            <div className="p-6 text-sm text-slate-500 text-center border rounded-lg bg-slate-50">
              No data for the selected projects in this date range.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 flex-wrap">
            {data && (
              <Button type="button" variant="outline" onClick={reset}>
                ← Pick again
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {!data && (
              <Button
                type="button"
                variant="outline"
                onClick={saveCurrent}
                disabled={selected.size < 2 || saving}
                data-testid="save-comparison-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bookmark className="w-4 h-4 mr-2" />}
                Save selection
              </Button>
            )}
            {!data && (
              <Button
                onClick={generate}
                disabled={selected.size < 2 || loading}
                data-testid="compare-generate-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                Generate ({selected.size} selected)
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectComparisonDialog;
